"use client"

import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useEffect } from "react"
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"

type Pagy = {
  page: number
  last: number
  previous?: number | null
  next?: number | null
}

interface PaginationAdvancedProps {
  pagy: Pagy
}

export function Pagy({ pagy }: PaginationAdvancedProps) {
  const { url } = usePage()
  const [pageInput, setPageInput] = React.useState(String(pagy.page))

  const buildUrl = (page: number) => {
    const u = new URL(url, window.location.origin)
    u.searchParams.set("page", String(page))
    return u.pathname + u.search
  }

  const goToPage = (page: number) => {
    if (page < 1 || page > pagy.last) return
    router.visit(buildUrl(page), {
      preserveScroll: true,
      preserveState: true,
    })
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const page = Number(pageInput)
    if (!Number.isNaN(page)) {
      goToPage(page)
    }
  }
  useEffect(() => {
    setPageInput(String(pagy.page))
  }, [pagy.page])

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t px-3 py-2 text-sm">
      {/* Page info */}
      <div className="text-muted-foreground">
        Page <strong>{pagy.page}</strong> of <strong>{pagy.last}</strong>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2">
        {/* First */}
        <Button
          variant="outline"
          size="icon"
          disabled={pagy.page === 1}
          onClick={() => goToPage(1)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        {/* Previous */}
        <Button
          variant="outline"
          size="icon"
          disabled={!pagy.previous}
          onClick={() => pagy.previous && goToPage(pagy.previous)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Jump */}
        <form onSubmit={onSubmit} className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={pagy.last}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            className="w-20"
          />
          <Button type="submit" variant="secondary">
            Go
          </Button>
        </form>

        {/* Next */}
        <Button
          variant="outline"
          size="icon"
          disabled={!pagy.next}
          onClick={() => pagy.next && goToPage(pagy.next)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Last */}
        <Button
          variant="outline"
          size="icon"
          disabled={pagy.page === pagy.last}
          onClick={() => goToPage(pagy.last)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

